import React, { useState, useEffect, useCallback } from 'react';
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Input,
  Space,
  Tag,
  Progress,
  Dropdown,
  Typography,
  Tooltip,
  Avatar,
  Select,
  DatePicker,
  Modal,
  Form,
  Steps,
  Descriptions,
  Badge,
  Collapse,
  Checkbox,
  Spin,
  Alert,
  message,
} from 'antd';
import {
  UserOutlined,
  PlusOutlined,
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  SaveOutlined,
  EditOutlined,
  DeleteOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  MoreOutlined,
  DownOutlined,
  UpOutlined,
  FilterOutlined,
  ClearOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiService, Client, User } from '../../../services/api';
import AddClientModal from './components/AddClientModal';
import EditClientModal from './components/EditClientModal';

const { Title, Text } = Typography;
const { Option } = Select;

type TableView = 'main' | 'subscription' | 'client-info' | 'updates';

const ManageClients: React.FC = () => {
  const [currentView, setCurrentView] = useState<TableView>('main');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isClientDetailModalVisible, setIsClientDetailModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedClientForDetails, setSelectedClientForDetails] = useState<Client | null>(null);
  const [selectedClientForEdit, setSelectedClientForEdit] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    searchText: '',
    tiers: [] as string[],
    paymentStatuses: [] as string[],
    subscriptionTypes: [] as string[],
    coaches: [] as string[],
    statuses: [] as string[],
    services: {
      training: false,
      nutrition: false,
      habitCoaching: false,
    },
    dateRange: null as any,
    onboardingProgress: null as any,
    daysToExpiry: null as any,
  });

  // Fetch clients from API
  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching clients from API...');
      const response = await apiService.getClients({
        page: 1,
        limit: 100
      });
      
      console.log('📊 Clients API response:', response);
      
      if (response.success && response.data) {
        const clientsData = response.data.clients || response.data;
        setClients(clientsData);
        setFilteredClients(clientsData);
        message.success(`Loaded ${clientsData.length} clients`);
      } else {
        throw new Error(response.error || 'Failed to fetch clients');
      }
    } catch (err: any) {
      console.error('❌ Failed to fetch clients:', err);
      setError(err.message || 'Failed to fetch clients');
      message.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Filter function
  const applyFilters = useCallback(() => {
    let filtered = [...clients];

    // Search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(client => {
        const user = client.user;
        const firstName = user?.firstName || '';
        const lastName = user?.lastName || '';
        const email = user?.email || '';
        
        return firstName.toLowerCase().includes(searchLower) ||
               lastName.toLowerCase().includes(searchLower) ||
               email.toLowerCase().includes(searchLower);
      });
    }

    // Tier filter
    if (filters.tiers.length > 0) {
      filtered = filtered.filter(client => filters.tiers.includes(client.tier));
    }

    // Payment status filter
    if (filters.paymentStatuses.length > 0) {
      filtered = filtered.filter(client => filters.paymentStatuses.includes(client.paymentStatus));
    }

    // Subscription type filter
    if (filters.subscriptionTypes.length > 0) {
      filtered = filtered.filter(client => 
        client.subscriptionType && filters.subscriptionTypes.includes(client.subscriptionType)
      );
    }

    // Status filter (active/inactive)
    if (filters.statuses.length > 0) {
      filtered = filtered.filter(client => {
        const status = client.isActive ? 'Active' : 'Inactive';
        return filters.statuses.includes(status);
      });
    }

    // Service filters
    if (filters.services.training || filters.services.nutrition || filters.services.habitCoaching) {
      filtered = filtered.filter(client => {
        const services = client.services || { training: false, nutrition: false, habitCoaching: false };
        return (
          (!filters.services.training || services.training) &&
          (!filters.services.nutrition || services.nutrition) &&
          (!filters.services.habitCoaching || services.habitCoaching)
        );
      });
    }

    // Onboarding progress filter
    if (filters.onboardingProgress) {
      const [min, max] = filters.onboardingProgress;
      filtered = filtered.filter(client => 
        client.onboardingProgress >= min && client.onboardingProgress <= max
      );
    }

    setFilteredClients(filtered);
  }, [clients, filters]);

  // Apply filters when filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      searchText: '',
      tiers: [],
      paymentStatuses: [],
      subscriptionTypes: [],
      coaches: [],
      statuses: [],
      services: {
        training: false,
        nutrition: false,
        habitCoaching: false,
      },
      dateRange: null,
      onboardingProgress: null,
      daysToExpiry: null,
    });
  };

  // Modal handlers
  const handleAddClient = () => {
    setIsModalVisible(true);
  };

  const handleAddClientSuccess = (newClient: Client) => {
    setClients(prev => [newClient, ...prev]);
    setFilteredClients(prev => [newClient, ...prev]);
    setIsModalVisible(false);
    message.success(`Client ${getClientName(newClient)} has been created successfully`);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleEditClient = (client: Client) => {
    console.log('🔄 ManageClients - handleEditClient called with client:', client);
    console.log('🔄 ManageClients - Client services from list:', client.services);
    
    setSelectedClientForEdit(client);
    setIsEditModalVisible(true);
    setIsClientDetailModalVisible(false); // Close details modal if open
  };

  const handleEditClientSuccess = (updatedClient: Client) => {
    // Update the client in both lists
    setClients(prev => prev.map(c => c.id === updatedClient.id ? updatedClient : c));
    setFilteredClients(prev => prev.map(c => c.id === updatedClient.id ? updatedClient : c));
    setIsEditModalVisible(false);
    setSelectedClientForEdit(null);
    message.success(`Client ${getClientName(updatedClient)} has been updated successfully`);
  };

  const handleEditModalCancel = () => {
    setIsEditModalVisible(false);
    setSelectedClientForEdit(null);
  };

  // Helper functions
  const getClientName = (client: Client): string => {
    const user = client.user;
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email || 'Unknown Client';
  };

  const getPaymentStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      'COMPLETED': 'green',
      'PENDING': 'orange',
      'PROCESSING': 'blue',
      'FAILED': 'red',
      'NONE': 'default',
      'CANCELLED': 'red',
      'REFUNDED': 'purple',
    };
    return colors[status] || 'default';
  };

  const getTierColor = (tier: string): string => {
    const colors: Record<string, string> = {
      'BASIC': 'blue',
      'STANDARD': 'green',
      'PREMIUM': 'orange',
      'ELITE': 'purple',
    };
    return colors[tier] || 'default';
  };

  // View selector component
  const ViewSelector = () => (
    <Card size="small" style={{ marginBottom: 16 }}>
      <Space>
        <Text strong>Table Views:</Text>
        <Button
          type={currentView === 'main' ? 'primary' : 'default'}
          size="small"
          onClick={() => setCurrentView('main')}
        >
          Main View
        </Button>
        <Button
          type={currentView === 'subscription' ? 'primary' : 'default'}
          size="small"
          onClick={() => setCurrentView('subscription')}
        >
          Subscription
        </Button>
        <Button
          type={currentView === 'client-info' ? 'primary' : 'default'}
          size="small"
          onClick={() => setCurrentView('client-info')}
        >
          Client Info
        </Button>
        <Button
          type={currentView === 'updates' ? 'primary' : 'default'}
          size="small"
          onClick={() => setCurrentView('updates')}
        >
          Updates
        </Button>
      </Space>
    </Card>
  );

  // Table columns based on current view
  const getColumns = (): ColumnsType<Client> => {
    const baseColumns: ColumnsType<Client> = [
      {
        title: 'Client',
        key: 'client',
        width: 200,
        render: (_, client) => (
          <Space>
            <Avatar size="small" icon={<UserOutlined />} />
            <div>
              <div 
                style={{ 
                  fontWeight: 500, 
                  color: '#1890ff', 
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onClick={() => {
                  setSelectedClientForDetails(client);
                  setIsClientDetailModalVisible(true);
                }}
              >
                {getClientName(client)}
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {client.user?.email}
              </Text>
            </div>
          </Space>
        ),
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      width: 100,
        render: (tier: string) => (
          <Tag color={getTierColor(tier)}>{tier}</Tag>
        ),
        filters: [
          { text: 'Basic', value: 'BASIC' },
          { text: 'Standard', value: 'STANDARD' },
          { text: 'Premium', value: 'PREMIUM' },
          { text: 'Elite', value: 'ELITE' },
        ],
        onFilter: (value, record) => record.tier === value,
      },
      {
        title: 'Payment Status',
        dataIndex: 'paymentStatus',
        key: 'paymentStatus',
        width: 130,
        render: (status: string) => (
          <Tag color={getPaymentStatusColor(status)}>
            {status.replace('_', ' ')}
          </Tag>
        ),
        filters: [
          { text: 'None', value: 'NONE' },
          { text: 'Pending', value: 'PENDING' },
          { text: 'Processing', value: 'PROCESSING' },
          { text: 'Completed', value: 'COMPLETED' },
          { text: 'Failed', value: 'FAILED' },
          { text: 'Cancelled', value: 'CANCELLED' },
          { text: 'Refunded', value: 'REFUNDED' },
        ],
        onFilter: (value, record) => record.paymentStatus === value,
      },
      {
        title: 'Onboarding',
        key: 'onboardingProgress',
        width: 120,
        render: (_, client) => (
          <Progress
            percent={client.onboardingProgress}
            size="small"
            format={(percent) => `${percent}%`}
          />
      ),
    },
    {
        title: 'Status',
        key: 'status',
        width: 100,
        render: (_, client) => (
          <Tag color={client.isActive ? 'green' : 'red'}>
            {client.isActive ? 'Active' : 'Inactive'}
          </Tag>
      ),
    },
    {
        title: 'Actions',
        key: 'actions',
      width: 120,
        render: (_, client) => (
          <Space>
            <Tooltip title="View Details">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedClientForDetails(client);
                  setIsClientDetailModalVisible(true);
                }}
              />
            </Tooltip>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'edit',
                    label: 'Edit Client',
                    icon: <EditOutlined />,
                  },
                  {
                    key: 'assign',
                    label: 'Assign Coach',
                    icon: <UserOutlined />,
                  },
                  {
                    key: 'delete',
                    label: 'Delete',
                    icon: <DeleteOutlined />,
                    danger: true,
                  },
                ],
                onClick: (menuInfo) => {
                  if (menuInfo.key === 'edit') {
                    handleEditClient(client);
                  } else if (menuInfo.key === 'assign') {
                    // TODO: Implement assign coach functionality
                    message.info('Assign coach functionality coming soon');
                  } else if (menuInfo.key === 'delete') {
                    // TODO: Implement delete functionality
                    message.info('Delete functionality coming soon');
                  }
                },
              }}
              trigger={['click']}
            >
              <Button size="small" icon={<MoreOutlined />} />
            </Dropdown>
        </Space>
      ),
    },
    ];

    // Add view-specific columns
    if (currentView === 'subscription') {
      baseColumns.splice(3, 0, {
        title: 'Subscription',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      width: 120,
        render: (type: string | null) => (
          <Tag>{type || 'None'}</Tag>
        ),
      });

      baseColumns.splice(4, 0, {
        title: 'Price',
      dataIndex: 'agreedPrice',
      key: 'agreedPrice',
        width: 100,
        render: (price: number | null) => (
          <Text>{price ? `$${price}` : 'N/A'}</Text>
        ),
      });
    }

    if (currentView === 'client-info') {
      baseColumns.splice(3, 0, {
          title: 'Phone',
          key: 'phone',
          width: 120,
        render: (_, client) => (
          <Text>{client.user?.phoneNumber || 'N/A'}</Text>
        ),
      });

      baseColumns.splice(4, 0, {
        title: 'Coach',
        key: 'coach',
        width: 150,
        render: (_, client) => {
          const coach = client.assignedCoach;
          if (coach?.user) {
            return `${coach.user.firstName || ''} ${coach.user.lastName || ''}`.trim() || coach.user.email;
          }
          return 'Unassigned';
        },
      });
    }

    return baseColumns;
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading clients...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        message="Error Loading Clients"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={fetchClients} icon={<ReloadOutlined />}>
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            <UserOutlined /> Manage Clients ({filteredClients.length})
          </Title>
          <Text type="secondary">
            Comprehensive client management and tracking
          </Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchClients}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddClient}>
              Add Client
            </Button>
            <Button icon={<DownloadOutlined />}>
              Export
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <Input
              placeholder="Search by name or email..."
              prefix={<SearchOutlined />}
              value={filters.searchText}
              onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
              allowClear
            />
          </Col>
          <Col span={4}>
            <Select
              mode="multiple"
              placeholder="Filter by tier"
                        value={filters.tiers}
              onChange={(value) => setFilters(prev => ({ ...prev, tiers: value }))}
              style={{ width: '100%' }}
            >
              <Option value="BASIC">Basic</Option>
              <Option value="STANDARD">Standard</Option>
              <Option value="PREMIUM">Premium</Option>
              <Option value="ELITE">Elite</Option>
            </Select>
                    </Col>
          <Col span={4}>
            <Select
              mode="multiple"
              placeholder="Payment status"
                        value={filters.paymentStatuses}
              onChange={(value) => setFilters(prev => ({ ...prev, paymentStatuses: value }))}
              style={{ width: '100%' }}
            >
              <Option value="NONE">None</Option>
              <Option value="PENDING">Pending</Option>
              <Option value="COMPLETED">Completed</Option>
              <Option value="FAILED">Failed</Option>
            </Select>
                    </Col>
          <Col span={4}>
            <Select
              mode="multiple"
              placeholder="Status"
              value={filters.statuses}
              onChange={(value) => setFilters(prev => ({ ...prev, statuses: value }))}
              style={{ width: '100%' }}
            >
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
                    </Col>
          <Col span={4}>
            <Space>
              <Button
                icon={<ClearOutlined />}
                onClick={clearAllFilters}
                disabled={Object.values(filters).every(v => 
                  Array.isArray(v) ? v.length === 0 : !v
                )}
              >
                Clear
              </Button>
              <Button
                icon={filtersExpanded ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setFiltersExpanded(!filtersExpanded)}
              >
                More
              </Button>
            </Space>
                    </Col>
                  </Row>

        {filtersExpanded && (
                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={6}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Services:</Text>
              <Space direction="vertical">
                        <Checkbox
                          checked={filters.services.training}
                          onChange={(e) => setFilters(prev => ({ 
                            ...prev, 
                            services: { ...prev.services, training: e.target.checked }
                          }))}
                        >
                  Training
                        </Checkbox>
                        <Checkbox
                          checked={filters.services.nutrition}
                          onChange={(e) => setFilters(prev => ({ 
                            ...prev, 
                            services: { ...prev.services, nutrition: e.target.checked }
                          }))}
                        >
                  Nutrition
                        </Checkbox>
                        <Checkbox
                          checked={filters.services.habitCoaching}
                          onChange={(e) => setFilters(prev => ({ 
                            ...prev, 
                            services: { ...prev.services, habitCoaching: e.target.checked }
                          }))}
                        >
                          Habit Coaching
                        </Checkbox>
              </Space>
                    </Col>
                  </Row>
        )}
      </Card>

      {/* View Selector */}
      <ViewSelector />

      {/* Table */}
      <Card>
        <Table<Client>
          dataSource={filteredClients}
          columns={getColumns()}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          pagination={{
            total: filteredClients.length,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} clients`,
          }}
          scroll={{ x: 1200 }}
          size="small"
        />
      </Card>

      {/* Client Detail Modal */}
      <Modal
        title="Client Details"
        open={isClientDetailModalVisible}
        onCancel={() => {
          setIsClientDetailModalVisible(false);
          setSelectedClientForDetails(null);
        }}
        width={800}
        footer={[
          <Button key="close" onClick={() => setIsClientDetailModalVisible(false)}>
            Close
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => selectedClientForDetails && handleEditClient(selectedClientForDetails)}
          >
            Edit Client
          </Button>,
        ]}
      >
        {selectedClientForDetails && (
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Name">
              {getClientName(selectedClientForDetails)}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {selectedClientForDetails.user?.email}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
              {selectedClientForDetails.user?.phoneNumber || 'N/A'}
              </Descriptions.Item>
            <Descriptions.Item label="Tier">
              <Tag color={getTierColor(selectedClientForDetails.tier)}>
                {selectedClientForDetails.tier}
              </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Payment Status">
              <Tag color={getPaymentStatusColor(selectedClientForDetails.paymentStatus)}>
                    {selectedClientForDetails.paymentStatus}
                  </Tag>
                </Descriptions.Item>
            <Descriptions.Item label="Subscription">
              {selectedClientForDetails.subscriptionType || 'None'}
                </Descriptions.Item>
            <Descriptions.Item label="Agreed Price">
              {selectedClientForDetails.agreedPrice ? `$${selectedClientForDetails.agreedPrice}` : 'N/A'}
                </Descriptions.Item>
            <Descriptions.Item label="Onboarding Progress">
              <Progress percent={selectedClientForDetails.onboardingProgress} size="small" />
                </Descriptions.Item>
            <Descriptions.Item label="Services" span={2}>
              <Space>
                {selectedClientForDetails.services?.training && <Tag color="blue">Training</Tag>}
                {selectedClientForDetails.services?.nutrition && <Tag color="green">Nutrition</Tag>}
                {selectedClientForDetails.services?.habitCoaching && <Tag color="orange">Habit Coaching</Tag>}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Notes" span={2}>
              {selectedClientForDetails.notes || 'No notes'}
                </Descriptions.Item>
              </Descriptions>
        )}
      </Modal>

      {/* Bulk Actions */}
      {selectedRowKeys.length > 0 && (
        <Card
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <Space>
            <Text strong>{selectedRowKeys.length} selected</Text>
            <Button size="small" icon={<EditOutlined />}>
              Bulk Edit
            </Button>
            <Button size="small" icon={<UserOutlined />}>
              Assign Coach
            </Button>
            <Button size="small" icon={<DownloadOutlined />}>
              Export
            </Button>
            <Button
              size="small"
              onClick={() => setSelectedRowKeys([])}
            >
              Clear Selection
            </Button>
                </Space>
        </Card>
      )}

      {/* Add Client Modal */}
      <AddClientModal
        visible={isModalVisible}
        onCancel={handleModalCancel}
        onSuccess={handleAddClientSuccess}
      />

      {/* Edit Client Modal */}
      <EditClientModal
        visible={isEditModalVisible}
        client={selectedClientForEdit}
        onCancel={handleEditModalCancel}
        onSuccess={handleEditClientSuccess}
      />
    </div>
  );
};

export default ManageClients; 